import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { showEditDialog, showRestoreDialog } from '../../../../../components';
import { useDocumentLibraryStorage, useDocumentLibraryTranslations } from '../../../../../hooks';
import { DocumentLibraryContext } from '../../../../../providers';
import { FileManagerBreadcrumbs } from '../../FileManagerBreadcrumbs';
import { FolderAccessPlaceholder } from './FolderAccessPlaceholder';
export const FolderHeaderContent = ({ activeTabId, onManagePermissions }) => {
    const navigate = useNavigate();
    const { t } = useDocumentLibraryTranslations();
    const { parents, reloadParents } = React.useContext(DocumentLibraryContext);
    const storage = useDocumentLibraryStorage();
    const functionClient = useFunctionClient();
    const fullIds = parents ? parents.map((parentDirectory) => parentDirectory.id) : [];
    const fullNames = parents ? parents.map((parentDirectory) => parentDirectory.columns.name) : [];
    const folderName = fullNames[fullNames.length - 1];
    const currentFolder = parents ? parents[parents.length - 1] : undefined;
    const canModify = currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.permissions.includes('modify');
    const isArchivedTab = activeTabId === 'archived';
    const fileEntityType = (currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.columns.directory) ? t('common.folder') : t('common.file');
    const getSharedData = () => {
        let accounts = 0;
        let organizations = 0;
        let systemRoles = 0;
        let isPublic = false;
        try {
            if (currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.columns.permissions) {
                const permissions = JSON.parse(currentFolder.columns.permissions);
                isPublic = !!(permissions === null || permissions === void 0 ? void 0 : permissions.public);
                ['read', 'write', 'modify'].forEach((permission) => {
                    if (permissions[permission]) {
                        accounts += permissions[permission].accountIds.length;
                        organizations += permissions[permission].organizationIds.length;
                        systemRoles += permissions[permission].systemRoles.length;
                    }
                });
            }
        }
        catch (e) {
            console.error(e);
        }
        return {
            accounts,
            organizations,
            systemRoles,
            isPublic,
        };
    };
    const handleEditFolder = (row) => {
        showEditDialog({
            storage: storage.fileStorageClient,
            t,
            file: row,
            fileEntityType,
            onSuccess: () => reloadParents(),
        });
    };
    const handleBack = (directoryId) => {
        if (activeTabId) {
            const urlParams = new URLSearchParams();
            urlParams.set('tab', activeTabId);
            if (!directoryId) {
                navigate({
                    pathname: '.',
                    search: urlParams.toString(),
                });
            }
            if (directoryId) {
                navigate({
                    pathname: directoryId,
                    search: urlParams.toString(),
                });
            }
        }
    };
    return (React.createElement(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, sm: 4 },
            py: { xs: 3, sm: 4 },
            rowGap: 2,
            width: '100%',
        }, "data-test": 'folder-header-content' },
        React.createElement(Box, { sx: { width: '100%', display: 'flex', justifyContent: 'space-between' } },
            React.createElement(FileManagerBreadcrumbs, { firstBreadcrumb: t('documentPanel.title'), fullIds: fullIds, fullNames: fullNames, onGoToDirectory: handleBack }),
            currentFolder && isArchivedTab && (React.createElement(IconButton, { "aria-label": t('common.restore'), "data-test": 'restore-folder', onClick: () => showRestoreDialog({
                    storage,
                    t,
                    functionClient,
                    file: currentFolder,
                    fileEntityType: t('common.folder'),
                    onSuccess: handleBack,
                }), sx: { color: themeColors.text.inverse } },
                React.createElement(UnarchiveOutlinedIcon, null))),
            canModify && isArchivedTab ? undefined : (React.createElement(IconButton, { "aria-label": t('folderHeaderContent.editFolder'), "data-test": 'edit-folder', onClick: () => currentFolder && handleEditFolder(currentFolder), sx: { color: themeColors.text.inverse } },
                React.createElement(DriveFileRenameOutlineOutlinedIcon, null)))),
        React.createElement(Box, { sx: { display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } } },
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    flexBasis: { md: '67%', lg: '75%' },
                    maxWidth: { md: '67%', lg: '75%' },
                } },
                React.createElement(Typography, { variant: 'h4', "data-test": 'folder-name', title: folderName, sx: { mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: 'auto' } }, folderName),
                React.createElement(Box, { sx: { display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 } },
                    isArchivedTab ? (React.createElement(Typography, { variant: 'caption', "data-test": 'last-updated' }, t('folderHeaderContent.archivedDate', {
                        date: (currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.columns.archiveDate)
                            ? formatDateTime(new Date(currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.columns.archiveDate))
                            : undefined,
                    }))) : (React.createElement(Typography, { variant: 'caption', "data-test": 'last-updated' }, t('folderHeaderContent.lastUpdated', {
                        date: (currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.updated) ? formatDateTime(new Date(currentFolder.updated)) : undefined,
                    }))),
                    React.createElement("span", null, "\u2022"),
                    React.createElement(Typography, { variant: 'caption', "data-test": 'folder-owner' }, t('folderHeaderContent.ownedBy', { name: currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.columns.ownerDisplayName }))),
                (currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.columns.summary) && (React.createElement(Typography, { variant: 'body1', "data-test": 'folder-summary', title: currentFolder.columns.summary, sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: 'auto' } }, currentFolder.columns.summary))),
            !isArchivedTab && (React.createElement(Box, { sx: {
                    flexBasis: { xs: '100%', md: '50%', lg: '420px' },
                    height: '100%',
                    width: '100%',
                } },
                React.createElement(FolderAccessPlaceholder, { permissions: currentFolder === null || currentFolder === void 0 ? void 0 : currentFolder.permissions, sharedData: getSharedData(), onManagePermissions: onManagePermissions, currentFolder: currentFolder }))))));
};
